/**
 * BillingTicketPage — one ticket as billing sees it (#50 PR A, plan §2c).
 *
 * Reads GET /v1/billing/tickets/:jobId: the ticket's facts in the legacy per-ticket page's
 * order (site, tanks, times, notes, crew) and the billed lines from billable_events, priced
 * as Generate will price them. The API computes `editable` and the lock reason; a locked
 * ticket says why.
 *
 * #50 PR C (DEC-356): an EDIT_BILLING_TICKET holder can edit an editable ticket in place
 * (EditTicketForm); edited lines show "was X", billing-added lines say so, and the edit
 * history lists every save. That edit data comes beside the read model, never on the
 * invoice.
 *
 * Money via formatUsdFromCents (pre-tax); quantities are decimal strings shown as stored;
 * times local (DEC-004). The frontend gates are UX-only — the API enforces VIEW_INVOICES
 * and EDIT_BILLING_TICKET.
 */

import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ApiError } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { StatusLabel } from 'src/components/ui/status-label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';

import { formatUsdFromCents } from '../lib/money';
import { formatTimestamp, formatCalendarDate } from '../lib/timestamp';
import { useUserTimezone } from '../lib/use-user-timezone';
import { useBillingMonth } from '../lib/billing-period';
import { useBillingTicket, type TicketPageResponse } from '../features/billing/hooks';
import { useUserProfile } from '../lib/use-user-timezone';
import { EDIT_BILLING_TICKET_ROLES, VOID_BILLING_TICKET_ROLES } from '../lib/billing-roles';
import { EditTicketForm } from '../features/billing/EditTicketForm';
import { VoidTicketPanel } from '../features/billing/VoidTicketPanel';
import { TicketHistory } from '../features/billing/TicketHistory';
import { DriverConfirmedWarnings } from '../features/billing/DriverConfirmedWarnings';
import { invoiceStatusTone } from '../features/billing/status-tone';
import { billingMethodLabel, lockMessage, ticketLabel } from '../features/billing/ticket-format';
import { primaryLineTag } from '../features/billing/customer-prices';

/** Gap 6 (0250): a service date (a calendar day, no zone), or a dash before 0250. */
function serviceDay(d: string | null | undefined): string {
  return d == null ? '—' : formatCalendarDate(d);
}

function Field({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>{label}</span>
      <span style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>{value}</span>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section style={{ marginBottom: '24px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '10px' }}>{title}</h3>
      {children}
    </section>
  );
}

const GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: '14px',
};

function gauge(feet: number | null, inches: number | null): string {
  if (feet === null && inches === null) return '—';
  return `${String(feet ?? 0)}' ${String(inches ?? 0)}"`;
}

/** The billed volume: the PER_BBL line(s), else the primary capture's reviewed volume. */
function billedVolume(page: TicketPageResponse): string | null {
  const volume = page.lines.filter((l) => l.kind === 'VOLUME');
  if (volume.length === 1) return volume[0]?.quantity ?? null;
  if (volume.length > 1) return volume.map((l) => l.quantity).join(' + ');
  return page.capture.reviewedNetBbl;
}

export function BillingTicketPage(): React.JSX.Element {
  const params = useParams<{ customerKey: string; jobId: string }>();
  const navigate = useNavigate();
  const tz = useUserTimezone();
  const [monthValue] = useBillingMonth();
  const jobId = params.jobId ?? null;
  const { data, isLoading, isError, error, refetch } = useBillingTicket(jobId);
  const at = (iso: string | null): string => (iso === null ? '—' : formatTimestamp(iso, tz));
  // #50 PR C — UX gate only; the API enforces EDIT_BILLING_TICKET.
  const role = useUserProfile().data?.profile.role ?? '';
  const canEdit = EDIT_BILLING_TICKET_ROLES.has(role);
  // OPEN-156 — UX gate only; the API enforces VOID_BILLING_TICKET.
  const canVoid = VOID_BILLING_TICKET_ROLES.has(role);
  const [editing, setEditing] = React.useState(false);
  const [voiding, setVoiding] = React.useState(false);
  const edits = new Map((data?.lineEdits ?? []).map((e) => [e.eventId, e]));

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <Button
        variant="ghost"
        onClick={(): void => {
          void navigate(`/pipeline/${params.customerKey ?? ''}?month=${monthValue}`);
        }}
        style={{ marginBottom: '16px' }}
      >
        <ArrowLeft size={16} aria-hidden="true" style={{ marginRight: '6px' }} />
        Back to customer
      </Button>

      {isLoading && (
        <p role="status" style={{ color: 'var(--color-text-secondary)' }}>
          Loading ticket…
        </p>
      )}

      {isError && error instanceof ApiError && error.status === 404 && (
        <p style={{ color: 'var(--color-text-secondary)' }}>This ticket doesn&apos;t exist.</p>
      )}

      {isError && !(error instanceof ApiError && error.status === 404) && (
        <div style={{ color: 'var(--color-danger)' }}>
          <p style={{ marginBottom: '8px' }}>Couldn&apos;t load this ticket.</p>
          <Button
            variant="outline"
            onClick={(): void => {
              void refetch();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {data !== undefined && (
        <>
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>
              Ticket {ticketLabel(data.ticket.haulId, data.ticket.jobId)}
              <StatusLabel
                tone={invoiceStatusTone(data.ticket.status)}
                style={{
                  marginLeft: '10px',
                  fontSize: '14px',
                  fontWeight: 500,
                  verticalAlign: 'middle',
                }}
              >
                {data.ticket.status}
              </StatusLabel>
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
              {data.customer.operatorName ?? 'Unnamed customer'} · {data.ticket.jobType ?? '—'} (
              {billingMethodLabel(data.ticket.billingMethod)}) · Completed{' '}
              {at(data.ticket.completedAt)}
              {data.ticket.serviceDate != null && (
                <> · Billed on {serviceDay(data.ticket.serviceDate)}</>
              )}
            </p>
          </div>

          {data.lock !== null && (
            <div
              role="note"
              style={{
                borderRadius: 'var(--radius-card)',
                backgroundColor: 'var(--color-bg-secondary)',
                padding: '10px 14px',
                marginBottom: '20px',
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
              }}
            >
              {lockMessage(data.lock)}
              {data.lock.code === 'ON_INVOICE' && (
                <>
                  {' '}
                  <Link
                    to={`/invoices/${data.lock.invoiceId}`}
                    style={{ color: 'var(--color-link)' }}
                  >
                    Open invoice
                  </Link>
                </>
              )}
            </div>
          )}

          {data.voided !== null && (
            <div
              role="note"
              data-testid="ticket-voided-note"
              style={{
                borderRadius: 'var(--radius-card)',
                backgroundColor: 'var(--color-bg-secondary)',
                padding: '10px 14px',
                marginBottom: '20px',
                fontSize: '14px',
              }}
            >
              Voided{data.voided.byName !== null ? ` by ${data.voided.byName}` : ''} on{' '}
              {at(data.voided.at)}: {data.voided.reason ?? 'no reason recorded'}
            </div>
          )}

          {!editing && !voiding && ((canEdit && data.editable) || (canVoid && data.voidable)) && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {canEdit && data.editable && (
                <Button
                  onClick={(): void => {
                    setEditing(true);
                  }}
                >
                  Edit ticket
                </Button>
              )}
              {canVoid && data.voidable && (
                <Button
                  variant="outline"
                  onClick={(): void => {
                    setVoiding(true);
                  }}
                >
                  Void ticket…
                </Button>
              )}
            </div>
          )}
          {voiding && data.voidable && (
            <VoidTicketPanel
              jobId={data.ticket.jobId}
              onClose={(): void => {
                setVoiding(false);
              }}
            />
          )}
          {editing && data.editable && (
            <EditTicketForm
              // A fresh form whenever the ticket reloads, so it starts from the truth.
              key={JSON.stringify(data.lines.map((l) => [l.eventId, l.quantity]))}
              page={data}
              onSaved={(): void => {
                setEditing(false);
              }}
              onCancel={(): void => {
                setEditing(false);
              }}
              onConflict={(): void => {
                setEditing(false);
                void refetch();
              }}
            />
          )}

          <Section title="Charges">
            <div
              style={{
                borderRadius: 'var(--radius-card)',
                backgroundColor: 'var(--color-bg-secondary)',
                overflow: 'auto',
              }}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.lines.map((l) => {
                    // D-15: where the primary line's price came from (this page only; never
                    // an invoice). The job's method, else the line's (FLAT_RATE bills per load).
                    // The page has no company today, so the tag's day shows its year.
                    const tag = primaryLineTag(
                      l,
                      data.ticket.billingMethod ??
                        (l.billingMethod === 'FLAT_RATE' ? 'PER_LOAD' : l.billingMethod),
                      undefined,
                    );
                    return (
                      <TableRow key={l.eventId}>
                        <TableCell>
                          {l.description}
                          {edits.get(l.eventId)?.addedByBilling === true && (
                            <Badge variant="secondary" style={{ marginLeft: '8px' }}>
                              Added by billing
                            </Badge>
                          )}
                          {l.invoice !== null && (
                            <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                              On {l.invoice.number ?? 'an invoice'}
                              {l.invoice.status !== null ? ` (${l.invoice.status})` : ''}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {l.quantity}
                          {edits.get(l.eventId)?.originalQuantity != null && (
                            <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                              was {edits.get(l.eventId)?.originalQuantity}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{l.unitLabel}</TableCell>
                        <TableCell className="text-right">
                          {l.needsPricing ? 'Needs pricing' : formatUsdFromCents(l.rateCents ?? 0)}
                          {tag !== null && (
                            <div
                              aria-label="Price source"
                              style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}
                            >
                              {tag}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatUsdFromCents(l.amountCents)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={4} style={{ fontWeight: 600 }}>
                      Unbilled subtotal (pre-tax)
                    </TableCell>
                    <TableCell className="text-right" style={{ fontWeight: 600 }}>
                      {formatUsdFromCents(data.unbilledTotalCents)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Section>

          <Section title="Edit history">
            <TicketHistory history={data.history} timeZone={tz} />
          </Section>

          <Section title="Ticket">
            <div style={GRID}>
              <Field label="Product" value={data.ticket.product ?? '—'} />
              <Field label="Lease" value={data.site.lease ?? '—'} />
              <Field
                label="County"
                value={
                  data.site.county === null
                    ? '—'
                    : [data.site.county, data.site.countyState].filter((v) => v !== null).join(', ')
                }
              />
              <Field label="Legals" value={data.site.legals ?? '—'} />
              <Field label="Disposal" value={data.site.disposal ?? '—'} />
              <Field label="Capture method" value={data.capture.captureMethod ?? '—'} />
              <Field label="Captured volume (bbl)" value={data.capture.capturedNetBbl ?? '—'} />
              <Field label="Billed volume (bbl)" value={billedVolume(data) ?? '—'} />
              {data.capture.captureCount > 1 && (
                <Field label="Captures" value={String(data.capture.captureCount)} />
              )}
            </div>
          </Section>

          <Section title="Times">
            <div style={GRID}>
              <Field label="Start time" value={at(data.times.startTime)} />
              <Field label="Job start" value={at(data.times.jobStart)} />
              <Field label="Job end" value={at(data.times.jobEnd)} />
              <Field label="Disposal in" value={at(data.times.disposalIn)} />
              <Field label="Disposal out" value={at(data.times.disposalOut)} />
              <Field label="End time" value={at(data.times.endTime)} />
            </div>
          </Section>

          {data.tanks.length > 0 && (
            <Section title="Tanks">
              <div
                style={{
                  borderRadius: 'var(--radius-card)',
                  backgroundColor: 'var(--color-bg-secondary)',
                  overflow: 'auto',
                }}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tank size/type</TableHead>
                      <TableHead>Tank no.</TableHead>
                      <TableHead>Top gauge</TableHead>
                      <TableHead>Bottom gauge</TableHead>
                      <TableHead>Color cut</TableHead>
                      <TableHead className="text-right">H2O bbl</TableHead>
                      <TableHead className="text-right">Oil bbl</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.tanks.map((t, i) => (
                      <TableRow key={`${t.tankNo ?? 'tank'}-${String(i)}`}>
                        <TableCell>{t.sizeType ?? '—'}</TableCell>
                        <TableCell>{t.tankNo ?? '—'}</TableCell>
                        <TableCell>{gauge(t.topGaugeFeet, t.topGaugeInches)}</TableCell>
                        <TableCell>{gauge(t.bottomGaugeFeet, t.bottomGaugeInches)}</TableCell>
                        <TableCell>{t.colorCut ?? '—'}</TableCell>
                        <TableCell className="text-right">{t.waterBbl ?? '—'}</TableCell>
                        <TableCell className="text-right">{t.oilBbl ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Section>
          )}

          <Section title="Driver">
            <div style={GRID}>
              <Field label="Driver" value={data.crew.driverLabel ?? data.crew.driverName ?? '—'} />
              {data.crew.hoursMovePending != null && (
                <Field label="Hours" value={data.crew.hoursMovePending} />
              )}
              <Field label="Unit #" value={data.crew.unitNumber ?? '—'} />
              <Field label="Trailer" value={data.crew.trailerNumber ?? '—'} />
            </div>
            {data.driverNotes !== null && (
              <p style={{ marginTop: '12px', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                {data.driverNotes}
              </p>
            )}
            <DriverConfirmedWarnings warnings={data.driverConfirmedWarnings} tz={tz} />
          </Section>
        </>
      )}
    </div>
  );
}
